import { useEffect, useRef, useState } from 'react'
import { Popup, type Map as MlMap, type MapLayerMouseEvent } from 'maplibre-gl'
import { addTrackLayers, setTrackData } from './layers/track'
import { addReplayAlertLayers, LYR_REPLAY_ALERTS, setReplayAlertData } from './layers/replayAlerts'
import { pathToTrack, positionAtTime, type TripPathPoint } from '../lib/tripPath'
import type { TripReplayEvent } from '../panels/TripReplay'
import type { TripMapAlert } from '../reports/alerts/alertTypes'
import type { Alert } from '../lib/types'
import { CC, TONE_HEX } from '../cc/tokens'
import { istClock } from '../lib/istFormat'
import type { BasemapMode } from './basemaps'
import {
  createStandaloneMap, escapeHtml, fitTo, isLngLat, setGeoJson, standaloneStyle, styleGate,
  type LngLat, type StyleGate,
} from './standaloneMap'

/**
 * One trip on its own map: the driven GPS path, the geotag/halt events along it, the
 * alerts that fired during it, and a vehicle marker at `playhead`.
 *
 * Embeddable, unlike MapCanvas.tsx (a store-bound singleton) -- this takes everything as
 * props so Reports > Alerts can open it inside a drawer next to the alert table, and a
 * test can mount it against a fake maplibre. The path and alert layers are the SAME
 * modules the live replay uses (`layers/track.ts`, `layers/replayAlerts.ts`), so a trip
 * reads identically here and in the Command Centre; only the thin event/vehicle layers
 * are local.
 */

export interface TripMapProps {
  points: TripPathPoint[]
  events: TripReplayEvent[]
  alerts: TripMapAlert[]
  selectedAlertId: string | null
  onSelectAlert?: (id: string) => void
  /** Epoch ms; the vehicle marker interpolates along `points` to this instant. Null/
   *  undefined hides the marker. */
  playhead?: number | null
  /** Default 'osm' -- a report page may well be off the corridor the vector archive was
   *  cut to; 'vector' is honoured when `/api/geo` reported tiles (see standaloneStyle). */
  basemap?: BasemapMode
  /** Changing it re-fits the camera over path ∪ alerts ∪ events. Without one the camera
   *  fits whenever the data itself changes (and never on a playhead tick). */
  fitKey?: string
  className?: string
}

export const SRC_TRIP_EVENTS = 'trip-events'
export const LYR_TRIP_EVENTS = 'trip-events-dot'
export const SRC_TRIP_VEHICLE = 'trip-vehicle'
export const LYR_TRIP_VEHICLE_HALO = 'trip-vehicle-halo'
export const LYR_TRIP_VEHICLE = 'trip-vehicle-dot'

/** Default centre before any data: the Dhule corridor's middle, replaced by fitTo. */
const DEFAULT_CENTER: LngLat = [75.1, 21.4]

const EMPTY: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] }

/** `layers/replayAlerts.ts` takes the store's own `Alert` shape; a report alert carries
 *  the subset the map needs. Spread LAST so any extra fields a caller already has
 *  (`_groupCount` from collapseSameLegAlerts, `vehicle_id`) ride through to the popup. */
export function tripMapAlertToAlert(a: TripMapAlert): Alert {
  return { vehicle_id: null, cell: null, depot: null, route_id: null, detail: null, ...a, message: a.message ?? '' }
}

/** Geotag/halt events with a position. Halts are amber (the same `warn` tone the
 *  TripReplayBar's halt spans use), geotags the muted info blue, so the two read apart
 *  at a glance -- a truck that sat still is the thing a reviewer is usually looking for. */
export function tripEventFeatures(events: readonly TripReplayEvent[]): GeoJSON.FeatureCollection<GeoJSON.Point> {
  return {
    type: 'FeatureCollection',
    features: events
      .filter((e) => isLngLat(e.lon, e.lat))
      .map((e) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [e.lon as number, e.lat as number] },
        properties: {
          kind: e.kind,
          event: e.event ?? null,
          name: e.geofence_name ?? e.hub_code ?? null,
          ts: e.ts,
          dwell_sec: e.dwell_sec ?? null,
        },
      })),
  }
}

function alertPopupHtml(p: Record<string, unknown>): string {
  const sev = String(p.severity ?? 'INFO')
  const tone = sev === 'CRITICAL' ? TONE_HEX.danger : sev === 'WARN' ? TONE_HEX.warn : TONE_HEX.info
  const count = typeof p.group_count === 'number' && p.group_count > 1 ? ` · ×${p.group_count}` : ''
  const ts = typeof p.ts === 'number' ? p.ts : null
  return `<div style="font:12px/1.45 ui-sans-serif,system-ui,sans-serif;color:#0F172A;max-width:240px">`
    + `<div style="font-weight:600">${escapeHtml(String(p.kind ?? ''))}`
    + ` <span style="color:${tone};font-size:10.5px;letter-spacing:.06em">${escapeHtml(sev)}</span>${count}</div>`
    + `<div style="font-family:ui-monospace,monospace;font-size:11px;color:#475569">${istClock(ts)} IST</div>`
    + (p.message ? `<div style="margin-top:2px">${escapeHtml(String(p.message))}</div>` : '')
    + `</div>`
}

export function TripMap({
  points, events, alerts, selectedAlertId, onSelectAlert, playhead = null,
  basemap = 'osm', fitKey, className = 'h-full w-full',
}: TripMapProps): JSX.Element {
  const container = useRef<HTMLDivElement>(null)
  const mapRef = useRef<MlMap | null>(null)
  const gateRef = useRef<StyleGate | null>(null)
  const onSelectRef = useRef(onSelectAlert)
  onSelectRef.current = onSelectAlert
  // Bumped after a basemap swap so every data effect below re-adds its layers to the new
  // style (setStyle drops custom sources/layers; the add* helpers are idempotent).
  const [styleEpoch, setStyleEpoch] = useState(0)
  const appliedBasemap = useRef(basemap)
  // Which data the camera was last fitted to. Belongs to the map INSTANCE, so it is
  // cleared when that instance is removed: under StrictMode (dev) the construction effect
  // runs mount -> cleanup -> mount, the first fit queues on the map the cleanup removes,
  // and a surviving signature made the second, real map skip its fit -- the drawer opened
  // on the default centre with the trip and its alerts off-screen.
  const lastFit = useRef<string | null>(null)

  // -------------------------------------------------------------------- construction
  useEffect(() => {
    if (!container.current || mapRef.current) return
    const map = createStandaloneMap(container.current, appliedBasemap.current, DEFAULT_CENTER, 9)
    mapRef.current = map
    const gate = styleGate(map)
    gateRef.current = gate

    const hover = new Popup({ closeButton: false, closeOnClick: false, offset: 12, maxWidth: '260px' })
    gate.run('layers', () => {
      addTrackLayers(map)
      if (!map.getSource(SRC_TRIP_EVENTS)) map.addSource(SRC_TRIP_EVENTS, { type: 'geojson', data: EMPTY })
      if (!map.getLayer(LYR_TRIP_EVENTS)) {
        map.addLayer({
          id: LYR_TRIP_EVENTS,
          type: 'circle',
          source: SRC_TRIP_EVENTS,
          paint: {
            'circle-radius': ['case', ['==', ['get', 'kind'], 'halt'], 6, 4],
            'circle-color': ['case', ['==', ['get', 'kind'], 'halt'], TONE_HEX.warn, CC.info],
            'circle-opacity': 0.9,
            'circle-stroke-color': '#FFFFFF',
            'circle-stroke-width': 1,
          },
        })
      }
      addReplayAlertLayers(map)
      if (!map.getSource(SRC_TRIP_VEHICLE)) map.addSource(SRC_TRIP_VEHICLE, { type: 'geojson', data: EMPTY })
      if (!map.getLayer(LYR_TRIP_VEHICLE_HALO)) {
        map.addLayer({
          id: LYR_TRIP_VEHICLE_HALO,
          type: 'circle',
          source: SRC_TRIP_VEHICLE,
          paint: { 'circle-radius': 13, 'circle-color': '#2563EB', 'circle-opacity': 0.22 },
        })
      }
      if (!map.getLayer(LYR_TRIP_VEHICLE)) {
        map.addLayer({
          id: LYR_TRIP_VEHICLE,
          type: 'circle',
          source: SRC_TRIP_VEHICLE,
          paint: {
            'circle-radius': 7,
            'circle-color': '#2563EB',
            'circle-stroke-color': '#FFFFFF',
            'circle-stroke-width': 2.5,
          },
        })
      }
    })

    // Bound once, not per draw: handlers survive a setStyle (they hang off the map, not
    // the layer object) and MapLibre simply ignores a layer id that is absent.
    map.on('mouseenter', LYR_REPLAY_ALERTS, (e: MapLayerMouseEvent) => {
      const f = e.features?.[0]
      if (!f || f.geometry.type !== 'Point') return
      map.getCanvas().style.cursor = 'pointer'
      hover
        .setLngLat(f.geometry.coordinates as LngLat)
        .setHTML(alertPopupHtml(f.properties ?? {}))
        .addTo(map)
    })
    map.on('mouseleave', LYR_REPLAY_ALERTS, () => {
      map.getCanvas().style.cursor = ''
      hover.remove()
    })
    map.on('click', LYR_REPLAY_ALERTS, (e: MapLayerMouseEvent) => {
      const id = e.features?.[0]?.properties?.alert_id
      if (typeof id === 'string' && id) onSelectRef.current?.(id)
    })

    return () => {
      hover.remove()
      map.remove()
      mapRef.current = null
      gateRef.current = null
      lastFit.current = null
    }
  }, [])

  // ------------------------------------------------------------------- basemap swap
  useEffect(() => {
    const map = mapRef.current
    const gate = gateRef.current
    if (!map || !gate || appliedBasemap.current === basemap) return
    appliedBasemap.current = basemap
    gate.rearm()
    map.setStyle(standaloneStyle(basemap))
    setStyleEpoch((n) => n + 1)
  }, [basemap])

  // --------------------------------------------------------------------------- path
  useEffect(() => {
    const map = mapRef.current
    const gate = gateRef.current
    if (!map || !gate) return
    gate.run('path', () => {
      addTrackLayers(map)
      setTrackData(map, pathToTrack('trip', points, undefined, { showEnds: true }))
    })
  }, [points, styleEpoch])

  // ------------------------------------------------------------------------- events
  useEffect(() => {
    const map = mapRef.current
    const gate = gateRef.current
    if (!map || !gate) return
    gate.run('events', () => setGeoJson(map, SRC_TRIP_EVENTS, tripEventFeatures(events)))
  }, [events, styleEpoch])

  // ------------------------------------------------------------------------- alerts
  useEffect(() => {
    const map = mapRef.current
    const gate = gateRef.current
    if (!map || !gate) return
    gate.run('alerts', () => {
      addReplayAlertLayers(map)
      setReplayAlertData(map, alerts.map(tripMapAlertToAlert), selectedAlertId)
    })
  }, [alerts, selectedAlertId, styleEpoch])

  // ----------------------------------------------------------------------- playhead
  useEffect(() => {
    const map = mapRef.current
    const gate = gateRef.current
    if (!map || !gate) return
    gate.run('vehicle', () => {
      const pos = playhead == null ? null : positionAtTime(points, playhead)
      setGeoJson(map, SRC_TRIP_VEHICLE, pos
        ? {
          type: 'FeatureCollection',
          features: [{
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [pos.lon, pos.lat] },
            properties: { ts: pos.ts, speed_kph: pos.speed_kph, heading_deg: pos.heading_deg },
          }],
        }
        : EMPTY)
    })
  }, [points, playhead, styleEpoch])

  // ---------------------------------------------------------------------------- fit
  useEffect(() => {
    const map = mapRef.current
    const gate = gateRef.current
    if (!map || !gate) return
    const sig = fitKey ?? `auto|${points.length}|${alerts.length}|${events.length}`
    if (lastFit.current === sig) return
    const coords: LngLat[] = [
      ...points.filter((p) => isLngLat(p.lon, p.lat)).map((p): LngLat => [p.lon, p.lat]),
      ...alerts.filter((a) => isLngLat(a.lon, a.lat)).map((a): LngLat => [a.lon as number, a.lat as number]),
      ...events.filter((e) => isLngLat(e.lon, e.lat)).map((e): LngLat => [e.lon as number, e.lat as number]),
    ]
    // Nothing plottable yet (data still loading): leave lastFit unset so the first real
    // payload under the same key still fits.
    if (!coords.length) return
    lastFit.current = sig
    gate.run('fit', () => fitTo(map, coords, 40))
  }, [points, alerts, events, fitKey])

  // -------------------------------------------------------------- selection fly-to
  // On CHANGE only: the initial selection is already inside the fit above, and zooming
  // straight onto it would throw away the whole-trip context the drawer opens with.
  const prevSelected = useRef<string | null>(selectedAlertId)
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (prevSelected.current === selectedAlertId) return
    prevSelected.current = selectedAlertId
    const a = selectedAlertId ? alerts.find((x) => x.alert_id === selectedAlertId) : undefined
    if (!a || !isLngLat(a.lon, a.lat)) return
    map.flyTo({ center: [a.lon as number, a.lat as number], zoom: Math.max(map.getZoom(), 13), duration: 500 })
  }, [selectedAlertId, alerts])

  return <div ref={container} className={className} data-testid="trip-map" />
}
